import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import RootRef from '@material-ui/core/RootRef';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import SortableTableHeadCell from 'components/common/SortableTableHeadCell';
import IconRefresh from 'components/icons/IconRefresh';

import InventoryTableEmpty from './InventoryTableEmpty';
import InventoryTableRow from './InventoryTableRow';

const styles = theme => ({
  paper: {
    position: 'relative',
    width: '100%',
    height: 'calc(100vh - 250px)',
    overflowX: 'auto',
    overflowY: 'visible',
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 176px)',
      transition: theme.transitions.create(['height'], {
        duration: theme.transitions.duration.short,
      }),
    },
  },
  paperMobileFilterExpanded: {
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 312px)',
    },
  },
  typography: {
    paddingLeft: 10,
    paddingBottom: 8,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  table: {
    minWidth: 500,
  },
  tableHead: {
    position: 'sticky',
    zIndex: 20,
    top: 0,
    '& th:first-child': {
      borderTopLeftRadius: 10,
    },
    '& th:last-child': {
      borderTopRightRadius: 10,
    },
    '& th': {
      position: 'relative',
      zIndex: 1,
    },
  },
  actionsCell: {
    paddingRight: 10,
    paddingLeft: 0,
    '&:last-child': {
      paddingRight: 10,
    },
  },
  iconButtonRefresh: {
    margin: '-14px 0 -18px 0',
    color: theme.palette.secondary[450],
  },
  iconRefresh: {
    width: 12,
    height: 12,
  },
});

const InventoryTable = ({
  items,
  count,
  classes,
  sort,
  sortBy,
  onSortingChange,
  loading,
  business,
  onProductCopySuccess,
  mobileFilterExpanded,
  onScroll,
  scrollRef,
}) => (
  <div>
    <Typography className={classes.typography}>
      {(loading === 0 || items.length > 0) && (
        ` (${count})`
      )}
    </Typography>
    <RootRef rootRef={scrollRef}>
      <Paper
        className={classNames(classes.paper, {
          [classes.paperMobileFilterExpanded]: mobileFilterExpanded,
        })}
        onScroll={onScroll}
      >
      {(loading === 0 && items.length === 0) &&
        <InventoryTableEmpty />
      }
      {items.length > 0 &&
        <Table className={classes.table}>
        <TableHead
          className={classes.tableHead}
        >
          <TableRow>
            <TableCell
              colSpan="2"
              className={classes.codesCell}
            >
              Image
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Product Name"
                sortField="Product Name"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Serial / Model"
                sortField="Model"
                tooltipLabel="Serial / Model"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Vendor"
                sortField="Vendor Name"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell
              className={classes.codesCell}
            >
              <SortableTableHeadCell
                label="Category"
                sortField="Category Name"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell
              className={classes.codesCell}
            >
              <SortableTableHeadCell
                label="Condition"
                sortField="Condition Name"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              Cases in Stock
            </TableCell>
            <TableCell>
              Units per Case
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Total Units"
                sortField="Total Quantity"
                tooltipLabel="Products Quantity"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Item Cost"
                sortField="Last Arrival Stock Line Price"
                tooltipLabel="Last Added Item Price"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Retail Price"
                sortField="Last Arrival Stock Line Retail Price"
                tooltipLabel="Retail Price"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              <SortableTableHeadCell
                label="Value"
                sortField="Total Price"
                tooltipLabel="Inventory Price"
                sortBy={sortBy}
                sort={sort}
                onSortingChange={onSortingChange}
                loading={loading}
              />
            </TableCell>
            <TableCell>
              HCPCS Codes
            </TableCell>
            <TableCell>
              Facility
            </TableCell>
            <TableCell>
              Warehouse
            </TableCell>
            <TableCell
              numeric
              className={classes.actionsCell}
            >
              <IconButton
                className={classes.iconButtonRefresh}
                onClick={() => onSortingChange(null)}
                tooltipContent="Clear Sorting"
              >
                <IconRefresh className={classes.iconRefresh} />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <InventoryTableRow
              key={item.ID}
              {...item}
              business={business}
              onProductCopySuccess={onProductCopySuccess}
            />
          ))}
        </TableBody>
        </Table>
      }
      </Paper>
    </RootRef>
  </div>
);

InventoryTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  count: PropTypes.number.isRequired,
  sort: PropTypes.oneOf(['asc', 'desc']),
  sortBy: PropTypes.string,
  onSortingChange: PropTypes.func,
  loading: PropTypes.number,
  onProductCopySuccess: PropTypes.func,
  classes: PropTypes.shape({
    paper: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
    actionsCell: PropTypes.string.isRequired,
    iconButtonRefresh: PropTypes.string.isRequired,
    iconRefresh: PropTypes.string.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
};

InventoryTable.defaultProps = {
  sort: null,
  sortBy: null,
  onSortingChange: null,
  loading: 0,
  business: {},
  onProductCopySuccess: () => {},
};

export default withStyles(styles)(InventoryTable);



// WEBPACK FOOTER //
// ./src/components/inventory/InventoryTable/InventoryTable.js